import React from 'react';
import {
    VenueAnalysisData,
    VenueAnalysisVenueTypeData,
} from 'widgets/venueAnalysis/venueAnalysisAggregatedData';

import {
    computeBarWidths,
    getSortedVenueTypes,
    VenueAnalysisVenueDisplayData,
} from 'widgets/venueAnalysis/grid/gridData';
import { VenueAnalysisBar } from 'widgets/venueAnalysis/grid/VenueAnalysisBar';

import './VenueTypeCapsuleContentFront.scss';
import { useDelayedHover } from '@instinet-analytics/core-lib';
import classNames from 'classnames';

const BarExpandThresholdPercent = 50;

export interface VenueTypeCapsuleContentFrontProps {
    data: VenueAnalysisData;
    showOrderData: boolean;
    onBarClicked: (
        view: 'back-historical' | 'back-today' | 'back-order'
    ) => void;
}

export function VenueTypeCapsuleContentFront(
    props: VenueTypeCapsuleContentFrontProps
) {
    const { data, showOrderData, onBarClicked } = props;

    const [onMouseEnter, onMouseLeave, flippable] = useDelayedHover(0);

    const width = 200;

    const {
        today: todayDisplayData,
        hist: histDisplayData,
        order: orderDisplayData,
    } = toVenueTypeCapsuleDisplayData(data);

    const cssClasses = classNames(
        'VenueTypeCapsuleContentFront',
        flippable && 'flippable'
    );

    return (
        <div className={cssClasses}>
            <Bar
                className="historical-bar"
                label="Historical"
                venues={histDisplayData}
                onClick={() => onBarClicked('back-historical')}
            />
            <Bar
                className="today-bar"
                label="Today"
                venues={todayDisplayData}
                onClick={() => onBarClicked('back-today')}
            />
            {showOrderData && (
                <Bar
                    className="order-bar"
                    label="Order"
                    venues={orderDisplayData}
                    onClick={() => onBarClicked('back-order')}
                />
            )}
        </div>
    );

    function Bar(props: {
        className: string;
        label: string;
        venues: VenueAnalysisVenueDisplayData[];
        onClick: () => void;
    }) {
        const { label, venues, onClick, className } = props;
        return (
            <div
                className={`bar ${className}`}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <div className="label">{label}</div>
                <div className="content">
                    {venues.map((venueType) => (
                        <VenueAnalysisBar
                            width={width}
                            data={venueType}
                            showVenues={false}
                            key={venueType.name}
                            highlightMode={'VenueType'}
                            highlightOnHover={true}
                            barExpandThresholdPercent={
                                BarExpandThresholdPercent
                            }
                        />
                    ))}
                </div>
            </div>
        );
    }
}

export function toVenueTypeCapsuleDisplayData(data: VenueAnalysisData) {
    const toDisplayData: (
        vtd?: VenueAnalysisVenueTypeData[]
    ) => VenueAnalysisVenueDisplayData[] = (vtd) =>
        vtd ? computeBarWidths(getSortedVenueTypes(vtd)) : [];

    const hist = toDisplayData(data?.mktVolHist?.venueTypes);
    const today = toDisplayData(data?.mktVolRealTime?.venueTypes);
    const order = toDisplayData(data?.orderVenue?.venueTypes);

    return { hist, today, order };
}
